/* eslint-disable @typescript-eslint/no-explicit-any */
import { useSubscription } from '../hooks/useSubscription';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/shared/components/ui/card';
import { Badge } from '@/shared/components/ui/badge';
import { Progress } from '@/shared/components/ui/progress';
import { cn } from '@/infrastructure/lib/utils';
import { Separator } from '@/shared/components/ui/separator';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/shared/components/ui/accordion';
import { Button } from '@/shared/components/ui/button';
import { Calendar, CreditCard, TrendingUp, Users, Database, MessageSquare, Zap, HardDrive, Eye, EyeOff, ArrowUpRight, Coins } from 'lucide-react';
import { format } from 'date-fns';
import { ptBR, enUS } from 'date-fns/locale';
import { useState } from 'react';
import { PlanSelector } from './PlanSelector';

export const SubscriptionSettings = () => {
  const { subscription, isLoading, isError } = useSubscription();
  const { t, i18n } = useTranslation();
  const locale = i18n.language === 'pt' ? ptBR : enUS;
  const lang = i18n.language === 'pt' ? 'pt' : 'en';
  
  // Map i18n language to locale format
  const getLocale = () => {
    const currentLang = i18n.language;
    if (currentLang.startsWith('pt')) return 'pt-BR';
    if (currentLang.startsWith('en')) return 'en-US';
    return 'en-US'; // Default fallback
  };
  
  const [showInNavbar, setShowInNavbar] = useState(() => {
    const stored = localStorage.getItem('prodgy-show-credit-counter');
    // Se não houver valor salvo, retorna true (visível por padrão)
    return stored === null ? true : stored === 'true';
  });
  const [showPlanSelector, setShowPlanSelector] = useState(false);

  const toggleNavbarVisibility = () => {
    const newValue = !showInNavbar;
    setShowInNavbar(newValue);
    localStorage.setItem('prodgy-show-credit-counter', String(newValue));
    // Dispatch custom event to notify navbar
    window.dispatchEvent(new Event('credit-counter-toggle'));
  };

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="animate-pulse text-center">
          <p className="text-lg text-muted-foreground">{t('common.loading')}</p>
        </div>
      </div>
    );
  }

  if (isError || !subscription) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="text-center">
          <p className="text-lg text-destructive">{t('subscription.error.loadFailed')}</p>
        </div>
      </div>
    );
  }

  const { subscribed_plan, consumption, status, start_date, due_date, trial_end_date } = subscription;
  
  // Calcula percentual de uso de créditos (com verificação de segurança)
  const creditUsagePercent = (consumption?.total && subscribed_plan?.credits?.limit) 
    ? (consumption.total / subscribed_plan.credits.limit) * 100 
    : 0;
  const creditsRemaining = (subscribed_plan?.credits?.limit && consumption?.total !== undefined)
    ? Math.round((subscribed_plan.credits.limit - consumption.total) * 100) / 100
    : 0;
  
  const getProgressColor = () => {
    if (creditUsagePercent >= 100) return "[&>div]:bg-red-500";
    if (creditUsagePercent >= 70) return "[&>div]:bg-orange-500";
    return "";
  };

  // Determina o status a ser exibido (quando plano e status são trial, mostra como active)
  const displayStatus = subscribed_plan?.name?.toLowerCase() === 'trial' && status === 'trial' ? 'active' : status;

  // Status badge color
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'bg-green-500';
      case 'trial':
        return 'bg-blue-500';
      case 'past_due':
        return 'bg-orange-500';
      case 'exhausted':
      case 'canceled':
      case 'expired':
        return 'bg-red-500';
      default:
        return 'bg-gray-500';
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h3 className="text-2xl font-bold">{t('subscription.title')}</h3>
        <p className="text-muted-foreground">{t('subscription.description')}</p>
      </div>

      {/* Plan Cards Row */}
      <div className="flex gap-4 items-stretch">
        {/* Plan Card */}
        <Card className="w-auto flex-shrink-0">
          <CardContent className="flex flex-col items-center justify-center gap-3 p-6">
            <div className="flex flex-col items-center gap-2">
              <p className="text-xs text-muted-foreground uppercase tracking-wide">{t('subscription.currentPlan')}</p>
              <p className="text-3xl font-bold">{subscribed_plan?.name ?? '-'}</p>
              <div className="flex items-center gap-1.5">
                <div className={`w-2 h-2 rounded-full ${getStatusColor(displayStatus)}`} />
                <p className="text-xs text-muted-foreground">
                  {t(`subscription.status.${displayStatus}`)}
                </p>
              </div>
            </div>
            <Button 
              variant="outline" 
              size="sm" 
              className="gap-1 h-7 text-xs px-2 w-full"
              onClick={() => setShowPlanSelector(true)}
            >
              {t('subscription.changePlan')}
              <ArrowUpRight className="h-3 w-3" />
            </Button>
          </CardContent>
        </Card>

        {/* Plan Overview */}
        <Card className="flex-1">
        <CardHeader>
          <div className="flex items-center justify-between gap-4">
            <div className="flex-1 min-w-0">
              <CardTitle>{subscribed_plan?.metadata?.badge?.[lang] ?? '-'}</CardTitle>
              <CardDescription className="line-clamp-2">{subscribed_plan?.description?.[lang] ?? '-'}</CardDescription>
            </div>
            <div className="text-right flex-shrink-0">
              <p className="text-3xl font-bold whitespace-nowrap">
                {i18n.language === 'pt' 
                  ? `R$ ${subscribed_plan?.price_brl?.toFixed(2) ?? '0.00'}`
                  : `$ ${subscribed_plan?.price_usd?.toFixed(2) ?? '0.00'}`
                }
              </p>
              <p className="text-sm text-muted-foreground whitespace-nowrap">
                {subscribed_plan?.billing_cycle ? t(`subscription.billingCycle.${subscribed_plan.billing_cycle}`) : '-'}
              </p>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Dates */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {start_date && (
              <div className="flex items-center gap-2">
                <Calendar className="h-4 w-4 text-muted-foreground" />
                <div>
                  <p className="text-sm text-muted-foreground">{t('subscription.startDate')}</p>
                  <p className="font-medium">{format(new Date(start_date), 'PP', { locale })}</p>
                </div>
              </div>
            )}
            {!trial_end_date && due_date && (
              <div className="flex items-center gap-2">
                <Calendar className="h-4 w-4 text-muted-foreground" />
                <div>
                  <p className="text-sm text-muted-foreground">{t('subscription.dueDate')}</p>
                  <p className="font-medium">{format(new Date(due_date), 'PP', { locale })}</p>
                </div>
              </div>
            )}
            {trial_end_date && (
              <div className="flex items-center gap-2">
                <Calendar className="h-4 w-4 text-muted-foreground" />
                <div>
                  <p className="text-sm text-muted-foreground">{t('subscription.trialEndDate')}</p>
                  <p className="font-medium">{format(new Date(trial_end_date), 'PP', { locale })}</p>
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
      </div>

      {/* Credits Usage */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <CreditCard className="h-5 w-5" />
              <CardTitle>{t('subscription.credits.title')}</CardTitle>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={toggleNavbarVisibility}
                className="h-8 gap-2"
              >
                {showInNavbar ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                <span className="text-xs">{showInNavbar ? t('subscription.credits.hideInNavbar') : t('subscription.credits.showInNavbar')}</span>
              </Button>
              <Badge variant="outline">
                {creditsRemaining.toLocaleString(getLocale(), { minimumFractionDigits: 0, maximumFractionDigits: 2 })} {t('subscription.credits.remaining')}
              </Badge>
            </div>
          </div>
          <CardDescription>{subscribed_plan?.credits?.label?.[lang]}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">{t('subscription.credits.used')}</span>
              <span className="font-medium">
                {consumption?.total ?? 0} / {subscribed_plan?.credits?.limit ?? 0}
              </span>
            </div>
            <Progress 
              value={creditUsagePercent} 
              className={cn("h-2", getProgressColor())} 
            />
            <p className="text-xs text-muted-foreground">
              {creditUsagePercent.toFixed(1)}% {t('subscription.credits.utilized')}
            </p>
          </div>

          <Separator />

          {/* Period Consumption - Accordion */}
          <Accordion type="single" collapsible className="w-full">
            <AccordionItem value="consumption" className="border-0">
              <AccordionTrigger className="hover:no-underline py-2">
                <div className="flex items-center gap-2">
                  <h4 className="text-sm font-medium">{t('subscription.consumption.title')}</h4>
                  {consumption?.period_start && (
                    <span className="text-xs text-muted-foreground">
                      ({format(new Date(consumption.period_start), 'PP', { locale })})
                    </span>
                  )}
                </div>
              </AccordionTrigger>
              <AccordionContent>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3 pt-2">
                  {consumption && Object.entries(consumption).map(([category, values]) => {
                    // Ignorar campos que não são métricas de consumo
                    if (category === 'total' || category === 'period_start' || category === 'period_end') return null;
                    
                    if (typeof values === 'object' && values !== null) {
                      return Object.entries(values).map(([metric, value]) => {
                        if (typeof value !== 'number') return null;
                        
                        // Mapa de ícones para categorias/métricas conhecidas
                        const iconMap: { [key: string]: any } = {
                          agents: Zap,
                          playground: MessageSquare,
                          knowledge_base: Database,
                          products: Users,
                          embeddings: Database,
                          workspaces: Users,
                          chats: MessageSquare,
                          executions: Zap,
                        };
                        
                        const Icon = iconMap[category] || iconMap[metric] || Coins;
                        const translationKey = `subscription.consumption.${category}_${metric}`;
                        const fallbackKey = `subscription.consumption.${metric}`;
                        
                        return (
                          <div key={`${category}-${metric}`} className="p-3 rounded-lg border">
                            <div className="flex items-center gap-2 mb-2">
                              <Icon className="h-4 w-4 text-muted-foreground" />
                              <p className="text-xs font-medium">
                                {t(translationKey, { defaultValue: t(fallbackKey, { defaultValue: `${category} ${metric}` }) })}
                              </p>
                            </div>
                            <p className="text-xl font-bold">{value}</p>
                          </div>
                        );
                      });
                    }
                    return null;
                  })}
                </div>
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </CardContent>
      </Card>

      {/* Fixed Limits */}
      <Card>
        <CardHeader>
          <CardTitle>{t('subscription.fixedLimits.title')}</CardTitle>
          <CardDescription>{t('subscription.fixedLimits.description')}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-3">
            {subscribed_plan?.fixed_limits && Object.entries(subscribed_plan.fixed_limits).map(([key, limit]: [string, any]) => {
              if (!limit || typeof limit !== 'object' || !limit.max) return null;
              
              const isHistoryRetention = key.includes('retention') || key.includes('history');
              
              return (
                <div key={key} className="flex items-center justify-between p-3 rounded-lg border">
                  <span className="text-sm font-medium">{limit.label?.[lang] || key}</span>
                  <Badge>
                    {limit.max} {isHistoryRetention ? t('subscription.fixedLimits.days') : ''}
                  </Badge>
                </div>
              );
            })}
          </div>

          <Separator />

          {/* Credit Unit Costs - Accordion */}
          <Accordion type="single" collapsible className="w-full">
            <AccordionItem value="credit-costs" className="border-0">
              <AccordionTrigger className="hover:no-underline py-2">
                <h4 className="text-sm font-medium">{t('subscription.credits.unitCosts')}</h4>
              </AccordionTrigger>
              <AccordionContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 pt-2">
                  {subscribed_plan?.credits?.unit_costs && Object.entries(subscribed_plan.credits.unit_costs).map(([key, cost]: [string, any]) => {
                    if (!cost || typeof cost !== 'object' || cost.cost === undefined) return null;
                    
                    // Mapa de ícones para tipos conhecidos
                    const iconMap: { [key: string]: any } = {
                      agent_execution: Zap,
                      embedding: Database,
                      playground_chat: MessageSquare,
                      storage_mb: HardDrive,
                      integration_monthly: TrendingUp,
                    };
                    
                    const Icon = iconMap[key] || Coins;
                    
                    return (
                      <div key={key} className="flex items-center justify-between p-3 rounded-lg border">
                        <div className="flex items-center gap-2">
                          <Icon className="h-4 w-4 text-muted-foreground" />
                          <span className="text-sm">{cost.label?.[lang] || key}</span>
                        </div>
                        <Badge variant="secondary">
                          {cost.cost}
                        </Badge>
                      </div>
                    );
                  })}
                </div>
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </CardContent>
      </Card>

      {/* Plan Selector Sidebar */}
      <PlanSelector
        isOpen={showPlanSelector}
        onClose={() => setShowPlanSelector(false)}
        onSelectPlan={() => {
          setShowPlanSelector(false);
          // Refresh subscription data
          window.location.reload();
        }}
      />
    </div>
  );
};
